import { PushPinFill } from '@archipro-design/icons';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { cn } from '@rocco/utils/cn';
import { TileFollowButtonWithPopover } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

interface BrowseByBrandTileProps {
    professional: ProfessionalTileViewModel;
    isFeatured?: boolean;
    /** Callback to unpin a featured brand. Provided by the parent to avoid
     *  instantiating a useFetcher per tile. Undefined when user lacks permissions. */
    onUnpin?: (itemID: number, pin: boolean) => void;
}

/**
 * Tile component for Browse by Brand carousel.
 * Dimensions per Figma: 238px x 347px (~2:3 aspect ratio)
 */
export const BrowseByBrandTile = ({
    professional,
    isFeatured,
    onUnpin,
}: BrowseByBrandTileProps) => {
    const { isInDesignBoard } = useHandleFollow();
    const isFollowed = isInDesignBoard(professional.id, 'ProfessionalPin');
    const showUnpin = isFeatured && onUnpin;

    return (
        <OptionalLink
            to={professional.link}
            prefetch="intent"
            className="group relative block overflow-hidden rounded-lg"
        >
            {/* Container: aspect-238/347 per Figma specs */}
            <div className="relative aspect-238/347 w-full overflow-hidden rounded-lg">
                {/* Banner image */}
                <img
                    src={professional.image.src}
                    alt={professional.image.alt}
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-lg bg-gray-900/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                {/* Logo overlay - 79px circular, 20px from edges */}
                {professional.logo && (
                    <div className="absolute top-5 left-5 z-10">
                        <div
                            className={cn(
                                'flex size-19.75 items-center justify-center overflow-hidden rounded-full shadow-lg transition-transform duration-300 group-hover:scale-110',
                                !professional.logo.background && 'bg-white',
                            )}
                            style={{
                                backgroundColor:
                                    professional.logo.background || 'white',
                            }}
                        >
                            {professional.logo.image.src && (
                                <img
                                    src={professional.logo.image.src}
                                    alt={professional.logo.image.alt}
                                    className="size-14 object-contain"
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Follow button (visible on hover) */}
                <div className="invisible absolute top-4 right-4 z-10 opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
                    <TileFollowButtonWithPopover
                        pinId={professional.id}
                        pinType="ProfessionalPin"
                        isFollowed={isFollowed}
                    />
                </div>

                {/* Unpin button for admin users on featured brands */}
                {showUnpin && (
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUnpin(professional.id, false);
                                    }}
                                    className="absolute right-4 bottom-4 z-10 flex size-8 items-center justify-center rounded-full bg-white/90 shadow-xs transition-colors hover:bg-white"
                                    aria-label="Unpin from Featured Brands"
                                >
                                    <PushPinFill className="size-4 fill-gray-900" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" color="dark">
                                Unpin this brand
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        </OptionalLink>
    );
};
